import { useMemo, useRef, useState } from 'react';
import {
  PIECES,
  canFlip,
  distinctStates,
  generateShape,
  isSolved,
  makeRng,
  orient,
  sizeOf,
  stepLimit,
} from './tangram';

/**
 * 七巧板:把托盘里的 7 块板拖到上方,拼出深色剪影。
 *
 * - 拖到拼图板上松手,吸附到网格;拖出拼图板就放回托盘
 * - 点一下板子选中,可以转 90°(平行四边形还能翻面)
 * - 放一次、转一次、翻一次各算一步;步数用完没拼出来就算失败,可以看答案
 *
 * 规则与判定在 tangram.js,这里只管交互和画面。
 */

const BOARD_W = 11;
const BOARD_H = 9;
const TRAY_TOP = 9.4;
const VIEW_H = 14.4;
const TRAY_SCALE = 0.55;
// 托盘里 7 个位置(中心点),第一排放大块,第二排放小块
const TRAY_SLOTS = {
  L1: [1.4, 10.65],
  L2: [4.1, 10.65],
  M: [6.8, 10.65],
  SQ: [9.5, 10.65],
  S1: [2.75, 13.15],
  S2: [5.5, 13.15],
  P: [8.25, 13.15],
};

export const DIFFICULTIES = {
  easy: { label: '简单', slack: 12 },
  normal: { label: '普通', slack: 6 },
  hard: { label: '困难', slack: 2 },
};

/** 开局:每块板放在托盘里,朝向随机(同一个 seed 每次一样) */
function initialPieces(seed) {
  const rng = makeRng(seed * 7919 + 17);
  return PIECES.map((p) => {
    const states = distinctStates(p.id);
    return {
      id: p.id,
      state: states[Math.floor(rng() * states.length)],
      x: 0,
      y: 0,
      onBoard: false,
    };
  });
}

function polygonPoints(verts) {
  return verts.map(([x, y]) => `${x},${y}`).join(' ');
}

function toSvgPoint(svg, e) {
  const ctm = svg?.getScreenCTM?.();
  if (!ctm) return { x: 0, y: 0 }; // jsdom 没有布局信息
  const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(ctm.inverse());
  return { x: p.x, y: p.y };
}

function TangramGame({ seed, difficulty = 'normal', onNext }) {
  const { solution, target } = useMemo(() => generateShape(seed), [seed]);
  const [pieces, setPieces] = useState(() => initialPieces(seed));
  const limit = useMemo(
    () =>
      stepLimit(
        solution,
        Object.fromEntries(initialPieces(seed).map((p) => [p.id, p.state])),
        DIFFICULTIES[difficulty].slack,
      ),
    [solution, seed, difficulty],
  );
  const [steps, setSteps] = useState(0);
  const [selected, setSelected] = useState(null);
  const [drag, setDrag] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const svgRef = useRef(null);

  const solved = isSolved(pieces, target);
  const lost = !solved && steps >= limit;
  const playing = !solved && !lost;

  const update = (id, patch) =>
    setPieces((ps) => ps.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const takeStep = () => setSteps((s) => s + 1);

  // ---------- 拖动 ----------

  const onPointerDown = (e, piece) => {
    if (!playing) return;
    e.preventDefault();
    // 捕获挂在整个画布上:被拖的板会被挪到最上层(DOM 节点换位置),
    // 挂在板子自己身上的捕获会随之丢失,手指一滑出画布就收不到松手事件
    svgRef.current?.setPointerCapture?.(e.pointerId);
    const pt = toSvgPoint(svgRef.current, e);
    const { w, h } = sizeOf(orient(piece.id, piece.state));
    // 从托盘里拿起来时,板子放大到原尺寸、以手指为中心
    const x = piece.onBoard ? piece.x : pt.x - w / 2;
    const y = piece.onBoard ? piece.y : pt.y - h / 2;
    setDrag({ id: piece.id, x, y, grabX: pt.x - x, grabY: pt.y - y, startX: pt.x, startY: pt.y, moved: false });
  };

  const onPointerMove = (e) => {
    if (!drag) return;
    const pt = toSvgPoint(svgRef.current, e);
    const moved = drag.moved || Math.hypot(pt.x - drag.startX, pt.y - drag.startY) > 0.2;
    setDrag({ ...drag, x: pt.x - drag.grabX, y: pt.y - drag.grabY, moved });
  };

  const onPointerUp = () => {
    if (!drag) return;
    const piece = pieces.find((p) => p.id === drag.id);
    setDrag(null);

    if (!drag.moved) {
      // 点一下 = 选中 / 取消选中
      setSelected(selected === piece.id ? null : piece.id);
      return;
    }

    const { w, h } = sizeOf(orient(piece.id, piece.state));
    const centerY = drag.y + h / 2;
    if (centerY > BOARD_H) {
      // 拖出拼图板:放回托盘,不算步数
      update(piece.id, { onBoard: false });
      return;
    }
    const x = Math.min(Math.max(Math.round(drag.x), 0), BOARD_W - w);
    const y = Math.min(Math.max(Math.round(drag.y), 0), BOARD_H - h);
    if (!piece.onBoard || x !== piece.x || y !== piece.y) takeStep();
    update(piece.id, { x, y, onBoard: true });
    setSelected(piece.id);
  };

  // ---------- 旋转 / 翻面 ----------

  const turn = (flipIt) => {
    const piece = pieces.find((p) => p.id === selected);
    if (!piece || !playing) return;
    const before = sizeOf(orient(piece.id, piece.state));
    const state = flipIt
      ? { rot: piece.state.rot, flip: !piece.state.flip }
      : { rot: (piece.state.rot + 1) % 4, flip: piece.state.flip };
    const after = sizeOf(orient(piece.id, state));
    // 绕板子中心转,再吸附回网格
    const cx = piece.x + before.w / 2;
    const cy = piece.y + before.h / 2;
    const x = Math.min(Math.max(Math.round(cx - after.w / 2), 0), BOARD_W - after.w);
    const y = Math.min(Math.max(Math.round(cy - after.h / 2), 0), BOARD_H - after.h);
    update(piece.id, { state, x, y });
    takeStep();
  };

  const restart = () => {
    setPieces(initialPieces(seed));
    setSteps(0);
    setSelected(null);
    setShowAnswer(false);
  };

  // ---------- 画面 ----------

  const renderPiece = (piece) => {
    const def = PIECES.find((p) => p.id === piece.id);
    const verts = orient(piece.id, piece.state);
    const { w, h } = sizeOf(verts);
    const dragging = drag?.id === piece.id;
    let transform;
    if (dragging) {
      transform = `translate(${drag.x} ${drag.y})`;
    } else if (piece.onBoard) {
      transform = `translate(${piece.x} ${piece.y})`;
    } else {
      const [sx, sy] = TRAY_SLOTS[piece.id];
      transform = `translate(${sx - (w * TRAY_SCALE) / 2} ${sy - (h * TRAY_SCALE) / 2}) scale(${TRAY_SCALE})`;
    }
    return (
      <g
        key={piece.id}
        data-testid={`piece-${piece.id}`}
        data-on-board={piece.onBoard}
        transform={transform}
        onPointerDown={(e) => onPointerDown(e, piece)}
        style={{ cursor: playing ? 'grab' : 'default' }}
      >
        <polygon
          points={polygonPoints(verts)}
          fill={def.color}
          stroke={selected === piece.id ? '#3B302B' : '#6B5D53'}
          strokeWidth={selected === piece.id ? 3 : 1.5}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </g>
    );
  };

  // 被拖动或选中的板画在最上层
  const order = [...pieces].sort((a, b) => {
    const rank = (p) => (drag?.id === p.id ? 2 : selected === p.id ? 1 : 0);
    return rank(a) - rank(b);
  });

  const selectedPiece = pieces.find((p) => p.id === selected);
  const left = Math.max(limit - steps, 0);

  return (
    <div className="flex w-full flex-col items-center gap-2">
      <div className="flex w-full items-center justify-between text-sm">
        <span data-testid="steps-left" className={`font-bold ${left <= 3 && playing ? 'text-qiji-coral' : 'text-gray-800'}`}>
          还剩 {left} 步
        </span>
        <span className="text-gray-500">
          已用 {steps} / {limit} 步
        </span>
      </div>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${BOARD_W} ${VIEW_H}`}
        className="w-full max-w-[340px] select-none rounded-lg"
        style={{ touchAction: 'none' }}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {/* 拼图板 + 网格 */}
        <rect x={0} y={0} width={BOARD_W} height={BOARD_H} fill="#FFFCF5" />
        {Array.from({ length: BOARD_W + 1 }, (_, i) => (
          <line key={`v${i}`} x1={i} y1={0} x2={i} y2={BOARD_H} stroke="#EADFC8" strokeWidth={0.03} />
        ))}
        {Array.from({ length: BOARD_H + 1 }, (_, i) => (
          <line key={`h${i}`} x1={0} y1={i} x2={BOARD_W} y2={i} stroke="#EADFC8" strokeWidth={0.03} />
        ))}

        {/* 目标剪影:7 块答案同色填充,描同色边盖住拼缝 */}
        <g data-testid="target">
          {solution.map((p) => (
            <polygon
              key={p.id}
              points={polygonPoints(orient(p.id, p.state))}
              transform={`translate(${p.x} ${p.y})`}
              fill="#8A7B6E"
              stroke="#8A7B6E"
              strokeWidth={0.04}
              strokeLinejoin="round"
            />
          ))}
        </g>

        {/* 托盘 */}
        <rect x={0} y={TRAY_TOP} width={BOARD_W} height={VIEW_H - TRAY_TOP} fill="#F3ECDD" rx={0.3} />

        {order.map(renderPiece)}

        {/* 看答案:在剪影上画出每块的位置 */}
        {showAnswer &&
          solution.map((p) => (
            <polygon
              key={`ans-${p.id}`}
              points={polygonPoints(orient(p.id, p.state))}
              transform={`translate(${p.x} ${p.y})`}
              fill={PIECES.find((d) => d.id === p.id).color}
              fillOpacity={0.85}
              stroke="#3B302B"
              strokeWidth={1.5}
              vectorEffect="non-scaling-stroke"
              pointerEvents="none"
            />
          ))}
      </svg>

      {playing && (
        <div className="flex min-h-[2.75rem] items-center gap-2">
          {selectedPiece ? (
            <>
              <button
                type="button"
                onClick={() => turn(false)}
                className="rounded-lg border-2 border-black bg-qiji-mint px-4 py-2 text-base font-bold shadow-brutal-sm active:translate-y-0.5 active:shadow-none"
              >
                ↻ 转一转
              </button>
              {canFlip(selectedPiece.id) && (
                <button
                  type="button"
                  onClick={() => turn(true)}
                  className="rounded-lg border-2 border-black bg-white px-4 py-2 text-base font-bold shadow-brutal-sm active:translate-y-0.5 active:shadow-none"
                >
                  ⇋ 翻个面
                </button>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-500">把下面的板子拖到上面,拼出深色的形状。点一下板子可以旋转。</p>
          )}
        </div>
      )}

      {solved && (
        <div className="flex flex-col items-center gap-2">
          <p className="text-lg font-bold text-gray-900">🎉 拼出来啦!用了 {steps} 步</p>
          <button
            type="button"
            onClick={onNext}
            className="rounded-lg border-2 border-black bg-qiji-gold px-5 py-2 text-base font-bold shadow-brutal-sm"
          >
            再来一个
          </button>
        </div>
      )}

      {lost && (
        <div className="flex flex-col items-center gap-2">
          <p className="text-base font-bold text-gray-900">步数用完啦,差一点点!</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowAnswer(!showAnswer)}
              className="rounded-lg border-2 border-black bg-white px-3 py-2 text-sm font-bold shadow-brutal-sm"
            >
              {showAnswer ? '收起答案' : '看答案'}
            </button>
            <button
              type="button"
              onClick={restart}
              className="rounded-lg border-2 border-black bg-qiji-mint px-3 py-2 text-sm font-bold shadow-brutal-sm"
            >
              重来这一题
            </button>
            <button
              type="button"
              onClick={onNext}
              className="rounded-lg border-2 border-black bg-qiji-gold px-3 py-2 text-sm font-bold shadow-brutal-sm"
            >
              换一个图形
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TangramGame;
